import "./styledComponentsTheme";
import { setFocusStyleDefault } from "./focusStyleManager";

setFocusStyleDefault();

// Native Blueprint components
export type {
  ActionProps,
  DrawerProps,
  EditableTextProps,
  FileInputProps,
  HotkeyConfig,
  HotkeyProps,
  HotkeysContextInstance,
  HotkeysProps,
  HotkeysProviderProps,
  HTMLDivProps,
  HTMLInputProps,
  IntentProps,
  MaybeElement,
  MenuDividerProps,
  MiddlewareConfig,
  MultistepDialogNavPosition,
  NavbarDividerProps,
  NavbarGroupProps,
  NavbarHeadingProps,
  NavbarProps,
  NonSmallSize,
  OverlayProps,
  OverlaysProviderProps,
  PopperModifierOverrides,
  PortalProps,
  ProgressBarProps,
  RangeSliderProps,
  ResizeEntry,
  ResizeSensorProps,
  Size,
  TabId,
  UseHotkeysOptions,
} from "@blueprintjs/core";

export {
  Alignment,
  Breadcrumbs,
  Classes,
  DialogStep,
  Divider,
  Drawer,
  DrawerSize,
  EditableText,
  Elevation,
  FileInput,
  FocusStyleManager,
  getKeyCombo,
  Hotkey,
  Hotkeys,
  HotkeysContext,
  HotkeysProvider,
  IconSize,
  Intent,
  Keys,
  Label,
  MenuDivider,
  Navbar,
  NavbarDivider,
  NavbarGroup,
  NavbarHeading,
  OverlaysProvider,
  PopoverInteractionKind,
  PopoverPosition,
  Portal,
  PortalContext,
  PortalProvider,
  Pre,
  ProgressBar,
  RangeSlider,
  ResizeSensor,
  SpinnerSize,
  TabsExpander,
  useHotkeys,
  Utils,
} from "@blueprintjs/core";

export type { DateRange, DateRangeShortcut } from "@blueprintjs/datetime";

export { Classes as DateClasses, TimePrecision } from "@blueprintjs/datetime";

export type {
  CreateNewItem,
  ItemListPredicate,
  ItemListRenderer,
  ItemModifiers,
  ItemPredicate,
  ItemsEqualComparator,
  ItemsEqualProp,
  QueryListRendererProps,
} from "@blueprintjs/select";

export { Classes as SelectClasses, getActiveItem, getCreateNewItem } from "@blueprintjs/select";

// Component replacements
export type { AlertProps } from "./components/alert";
export { Alert } from "./components/alert";
export type { AnchorButtonProps, ButtonProps, ButtonVariant, IconButtonProps } from "./components/button";
export { AnchorButton, Button, IconButton } from "./components/button";
export type { AudioPlayerProps } from "./components/audioPlayer";
export { AudioPlayer } from "./components/audioPlayer";
export type { BreadcrumbProps } from "./components/breadcrumb";
export { Breadcrumb } from "./components/breadcrumb";
export type { ButtonGroupProps, ButtonGroupVariant } from "./components/buttonGroup";
export { ButtonGroup } from "./components/buttonGroup";
export type { CalloutProps, CalloutVariant } from "./components/callout";
export { Callout } from "./components/callout";
export type { CardProps } from "./components/card";
export { Card } from "./components/card";
export type { CarouselProps } from "./components/carousel";
export { Carousel } from "./components/carousel";
export type { CollapseProps } from "./components/collapse";
export type { CompoundTagProps } from "./components/compoundTag";
export { CompoundTag } from "./components/compoundTag";
export { Collapse } from "./components/collapse";
export type { DateDialProps } from "./components/dateDial";
export { DateDial } from "./components/dateDial";
export type { DateInputProps } from "./components/dateInput";
export { DateInput } from "./components/dateInput";
export type { DateRangeInputProps } from "./components/dateRangeInput";
export { DateRangeInput } from "./components/dateRangeInput";
export type { DatePickerProps } from "./components/datePicker";
export { DatePicker } from "./components/datePicker";
export type { DateRangePickerProps } from "./components/dateRangePicker";
export { DateRangePicker } from "./components/dateRangePicker";
export type { DialogProps, DialogStepButtonProps, MultistepDialogProps } from "./components/dialog";
export { Dialog, MultistepDialog } from "./components/dialog";
export type { DurationDialProps } from "./components/durationDial";
export { DurationDial } from "./components/durationDial";
export type { ErrorBoundaryProps } from "./components/errorBoundary";
export { ErrorBoundary } from "./components/errorBoundary";
export type { FormGroupProps } from "./components/formGroup";
export { FormGroup } from "./components/formGroup";
export type { FormAreaProps, FormScopeProps } from "./components/formScope";
export { FormArea, FormScope, FormScopeContext, getAutomationId, getComponentId, useAutomationId } from "./components/formScope";
export type { GridDialogProps } from "./components/gridDialog";
export { GridDialog } from "./components/gridDialog";
export type { GroupedRows } from "./components/gridView";
export type { HTMLSelectProps } from "./components/htmlSelect";
export { HTMLSelect } from "./components/htmlSelect";
export type { HTMLTableProps } from "./components/htmlTable";
export { HTMLTable } from "./components/htmlTable";
export type { IconName, IconProps } from "./components/icon";
export { Icon } from "./components/icon";
export type { InputButtonProps } from "./components/inputButton";
export { InputButton } from "./components/inputButton";
export type { InputGroupProps, InputGroupType, InputGroupVariant } from "./components/inputGroup";
export { InputGroup } from "./components/inputGroup";
export type { InputMaskProps } from "./components/inputMask";
export { InputMask } from "./components/inputMask";
export { InteractionModeEngine } from "./components/interactionModeEngine";
export type { ListOptionProps } from "./components/listOption";
export { ListOption } from "./components/listOption";
export type { MenuProps } from "./components/menu";
export { Menu } from "./components/menu";
export type { MenuItemProps } from "./components/menuItem";
export { MenuItem } from "./components/menuItem";
export type { MultiSelectListRenderer, MultiSelectListRendererProps, MultiSelectProps } from "./components/multiSelect";
export { MultiSelect } from "./components/multiSelect";
export type { NonIdealIconProps, NonIdealStateProps } from "./components/nonIdealState";
export { NonIdealIcon, NonIdealState } from "./components/nonIdealState";
export type { NumberDialProps } from "./components/numberDial";
export { NumberDial } from "./components/numberDial";
export type { OmnibarProps } from "./components/omnibar";
export { Omnibar } from "./components/omnibar";
export { Overlay } from "./components/overlay";
export type { QueryListProps } from "./components/queryList";
export { QueryList } from "./components/queryList";
export type { PanelStackProps } from "./components/panelStack";
export { PanelStack } from "./components/panelStack";
export type { Placement, PopoverProps, RenderTargetProps } from "./components/popover";
export { Popover } from "./components/popover";
export type { ProgressDialogProps } from "./components/progressDialog";
export { ProgressDialog } from "./components/progressDialog";
export type { RadioGroupProps } from "./components/radioGroup";
export { RadioGroup } from "./components/radioGroup";
export type { SelectProps } from "./components/select";
export { Select } from "./components/select";
export type { SliderProps } from "./components/slider";
export { Slider } from "./components/slider";
export type { StretchContainerProps } from "./components/stretchContainer";
export { StretchContainer } from "./components/stretchContainer";
export type { SpinnerProps } from "./components/spinner";
export { Spinner } from "./components/spinner";
export type { ShimmerProps, ShimmerTone } from "./components/shimmer";
export { Shimmer } from "./components/shimmer";
export type { SuggestProps } from "./components/suggest";
export { Suggest } from "./components/suggest";
export type { SvgProps } from "./components/svg";
export { Svg } from "./components/svg";
export type { TabProps, TabIdProps } from "./components/tab";
export { Tab } from "./components/tab";
export type { TabsProps } from "./components/tabs";
export { TabHeader, Tabs } from "./components/tabs";
export type { TagProps } from "./components/tag";
export { Tag } from "./components/tag";
export type { TagInputProps } from "./components/tagInput";
export { TagInput } from "./components/tagInput";
export type { TextAreaProps } from "./components/textArea";
export { TextArea } from "./components/textArea";
export type { TimeDialProps } from "./components/timeDial";
export { TimeDial } from "./components/timeDial";
export type { Toaster, ToastOptions, ToastProps } from "./components/toaster";
export { ForwardingNotifier, OverlayToaster, Toast, voidNotifier } from "./components/toaster";
export type { CheckboxCardProps, RadioCardProps } from "./components/toggleCards";
export { CheckboxCard, RadioCard } from "./components/toggleCards";
export type { CheckboxProps, RadioProps, SwitchProps } from "./components/toggles";
export { Checkbox, Radio, Switch } from "./components/toggles";
export type { ToneProps } from "./components/tone";
export { Tone, toneClass, toneToIntent } from "./components/tone";
export type { TooltipProps } from "./components/tooltip";
export { Tooltip } from "./components/tooltip";
export { ColorPicker } from "./components/colorPicker";
export type { ColorPickerProps } from "./components/colorPicker";
export { ColorSwatch } from "./components/colorSwatch";
export type { ColorSwatchProps } from "./components/colorSwatch";
export type { ItemInfoRenderer, ItemRendererProps, ItemTextRenderer, ListItemsProps, ListOptionRenderer, SelectPopoverProps } from "./components/useListItems";

// Custom components
export * from "./components/accordion";
export * from "./components/assistBorder";
export * from "./components/asyncMultiSelect";
export * from "./components/asyncMultiSelectFilter";
export * from "./components/asyncMultiSelectPopover";
export * from "./components/asyncSuggest";
export * from "./components/badge";
export * from "./components/cardStack";
export * from "./components/charts";
export * from "./components/columnFilter";
export * from "./components/copyButton";
export * from "./components/ellipsize";
export * from "./components/ellipsizedTd";
export * from "./components/tableCell";
export * from "./components/illustrationIcon";
export * from "./components/infiniteScroll";
export * from "./components/imageAreaCropper";
export * from "./components/imageZoomCropper";
export * from "./components/interactiveRow";
export * from "./components/grid";
export * from "./components/gridFeed";
export * from "./components/groupColumnFilter";
export * from "./components/lazyMultiSelect";
export * from "./components/lazySelectInput";
export * from "./components/liveAudioVisualizer";
export * from "./components/loadingMask";
export * from "./components/monthGrid";
export * from "./components/monthPicker";
export * from "./components/monthRangePicker";
export * from "./components/multiSelectFilter";
export * from "./components/navRail";
export * from "./components/pagination";
export * from "./components/pagingGrid";
export * from "./components/permissionDeniedNotice";
export * from "./components/portal";
export * from "./components/selectInput";
export * from "./components/signatureCanvas";
export * from "./components/singleSelectFilter";
export * from "./components/sortButton";
export * from "./components/step";
export * from "./components/stepConnector";
export * from "./components/stepper";
export * from "./components/tagList";
export * from "./components/theme";

// Utilities
export * from "./utils/abort";
export * from "./utils/canvas";
export * from "./utils/classes";
export * from "./utils/colorRange";
export * from "./utils/colors";
export * from "./utils/date";
export * from "./utils/debouncer";
export * from "./utils/duration";
export * from "./utils/focusables";
export * from "./utils/form";
export * from "./utils/hotkeyParser";
export * from "./utils/lazy";
export * from "./utils/misc";
export * from "./utils/react";
export * from "./utils/select";
export * from "./utils/shadowRootRange";
export * from "./utils/styles";
export * from "./utils/subscription";
export * from "./utils/sz";
export * from "./modernizr";

// Fun theme framework
export * from "./fun";

// Hooks
export * from "./hooks";

// Storage
export * from "./storagePartition";

export type * from "./types";

export const fontUrl = "https://fonts.googleapis.com/css?family=Lato:300,300i,400,400i,700,700i&display=swap";
